// packages/web/src/components/account/account-drawer.tsx
/**
 * AccountDrawer — right-slide drawer with account overview.
 *
 * 19BN Phase 2-4: shown when UserAvatar is clicked (authenticated). Contains
 * CreditsHero + quick links to recharge/invite/history/settings. Full sub-pages
 * (recharge flow, invite panel, credits history) are rendered as tabs within
 * this drawer — keeping the UX contained (no separate routes needed).
 *
 * Visual: spec 附录 A · 印章学术 · reuses message-center drawer CSS pattern.
 * Spec: docs/architecture/account-system-design.md §8.2, §9.6
 */

import { useEffect, useState } from 'react';
import { useStore } from '../../lib/store/index.js';
import { useT } from '../../i18n/use-t.js';
import { CreditsHero } from './credits-hero.js';
import { RechargeFlow } from './recharge-flow.js';
import { InvitePanel } from './invite-panel.js';
import { CreditsHistoryList } from './credits-history-list.js';
import { SubscriptionPanel } from './subscription-panel.js';
import { AccountSettingsPanel } from './account-settings-panel.js';

export type AccountTab = 'overview' | 'recharge' | 'subscription' | 'invite' | 'history' | 'settings';

export interface AccountDrawerProps {
  open: boolean;
  initialTab?: AccountTab;
  onClose: () => void;
}

export function AccountDrawer({ open, initialTab = 'overview', onClose }: AccountDrawerProps) {
  const t = useT();
  const currentUser = useStore((s) => s.currentUser);
  const [tab, setTab] = useState<AccountTab>(initialTab);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset tab to initialTab each time the drawer opens; key-reset alternative needs parent to own the open/initialTab prop
    if (open) setTab(initialTab);
  }, [initialTab, open]);

  // 19BN Phase D D1: pre-warm subscription state when the drawer opens so the
  // subscription tab doesn't flash "loading" on first click. invite panel
  // keeps its own fetch (independent lifecycle); subscription lives in the
  // slice because the overview + tab both read it.
  const refreshSubscription = useStore((s) => s.refreshSubscription);
  useEffect(() => {
    if (open && currentUser && !currentUser.isAnonymous) {
      void refreshSubscription();
    }
  }, [open, currentUser, refreshSubscription]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open || !currentUser || currentUser.isAnonymous) return null;

  return (
    <>
      <div
        className="message-center-overlay"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        aria-hidden
      />
      <aside
        className="message-center-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t('auth.profile')}
      >
        <div className="message-center-header">
          <h2 className="message-center-title">{t('auth.profile')}</h2>
          <button className="message-center-close-btn" onClick={onClose} aria-label={t('common.close')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="message-center-tabs" role="tablist">
          {([
            ['overview', t('account.balance')],
            ['recharge', t('account.recharge')],
            ['subscription', '订阅'],
            ['invite', t('auth.inviteCode')],
            ['history', t('account.creditsHistory')],
            ['settings', t('common.settings')],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              className={`message-center-tab${tab === key ? ' is-active' : ''}`}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="message-center-body" style={{ padding: 'var(--space-3xl)' }}>
          {tab === 'overview' && (
            <>
              <CreditsHero onRecharge={() => setTab('recharge')} />
              <div style={{ marginTop: 'var(--space-2xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
                <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
                  {currentUser.email}
                </div>
              </div>
            </>
          )}
          {tab === 'recharge' && <RechargeFlow />}
          {tab === 'subscription' && <SubscriptionPanel />}
          {tab === 'invite' && <InvitePanel />}
          {tab === 'history' && <CreditsHistoryList />}
          {tab === 'settings' && <AccountSettingsPanel />}
        </div>
      </aside>
    </>
  );
}
